<script setup>
import { computed } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { useTrack } from 'dashboard/composables';

import { BUS_EVENTS } from 'shared/constants/busEvents';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { COPILOT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';

import CopilotLoader from './CopilotLoader.vue';

const props = defineProps({
  isLastMessage: {
    type: Boolean,
    default: false,
  },
  message: {
    type: Object,
    required: true,
  },
  conversationInboxType: {
    type: String,
    required: true,
  },
});
const hasEmptyMessageContent = computed(() => !props.message?.content);

// True while tokens are still streaming in for this assistant reply.
const isStreaming = computed(() => Boolean(props.message?.streaming));

const showUseButton = computed(() => {
  return (
    !hasEmptyMessageContent.value &&
    props.message.reply_suggestion &&
    props.isLastMessage
  );
});

const messageContent = computed(() => {
  const formatter = new MessageFormatter(props.message.content);
  return formatter.formattedMessage;
});

const insertIntoRichEditor = computed(() => {
  return [INBOX_TYPES.WEB, INBOX_TYPES.EMAIL].includes(
    props.conversationInboxType
  );
});

const useCopilotResponse = () => {
  if (insertIntoRichEditor.value) {
    emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, props.message?.content);
  } else {
    emitter.emit(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, props.message?.content);
  }
  useTrack(COPILOT_EVENTS.USE_CAPTAIN_RESPONSE);
};
</script>

<template>
  <div
    class="flex flex-col gap-1.5 p-3 rounded-xl border border-n-weak bg-n-alpha-1 shadow-sm text-n-slate-12"
  >
    <div class="flex items-center gap-2 text-xs font-semibold text-n-violet-11">
      <span
        class="size-2 rounded-full bg-n-violet-9 shadow-[0_0_8px_1px_rgba(110,86,207,0.6)]"
      />
      {{ $t('CAPTAIN.NAME') }}
    </div>
    <CopilotLoader v-if="hasEmptyMessageContent && isStreaming" />
    <span v-else-if="hasEmptyMessageContent" class="text-n-ruby-11">
      {{ $t('CAPTAIN.COPILOT.EMPTY_MESSAGE') }}
    </span>
    <div
      v-else
      v-dompurify-html="messageContent"
      class="prose-sm break-words"
    />
    <div v-if="showUseButton" class="flex flex-row mt-1">
      <button
        class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white bg-gradient-to-br from-n-violet-9 to-n-iris-9 hover:brightness-110 transition-[filter]"
        @click="useCopilotResponse"
      >
        <i class="i-lucide-check size-3.5" />
        {{ $t('CAPTAIN.COPILOT.USE') }}
      </button>
    </div>
  </div>
</template>
